import { useEffect, useState } from 'react';

import { getByEmail } from '../settings/api';
import { fetchAvatarObjectUrl, fetchCurrentUser } from './api';
import type { Caller } from './models';

export type CurrentUserState = {
    status: 'loading' | 'ready' | 'error';
    user: Caller | null;
    daysOff: Set<string>;
    etag: string | null;
    error: string | null;
};

const initial: CurrentUserState = {
    status: 'loading',
    user: null,
    daysOff: new Set<string>(),
    etag: null,
    error: null,
};

export const useCurrentUserState = (configured: boolean, org: string, pat: string): CurrentUserState => {
    const [state, setState] = useState<CurrentUserState>(initial);

    useEffect(() => {
        if (!configured) {
            setState(initial);
            return;
        }

        let cancelled = false;
        let mintedAvatarUrl: string | null = null;
        setState({ ...initial, status: 'loading' });

        (async () => {
            try {
                const me = await fetchCurrentUser();
                if (cancelled) {
                    return;
                }

                const result = await getByEmail(me.email);
                if (cancelled) {
                    return;
                }

                setState({
                    status: 'ready',
                    user: { ...me, avatarObjectUrl: null },
                    daysOff: new Set(result?.member.daysOff ?? []),
                    etag: result?.etag ?? null,
                    error: null,
                });

                const avatarUrl = await fetchAvatarObjectUrl(org, pat);
                if (cancelled) {
                    if (avatarUrl) {
                        URL.revokeObjectURL(avatarUrl);
                    }
                    return;
                }
                if (avatarUrl) {
                    mintedAvatarUrl = avatarUrl;
                    setState((prev) => (prev.user ? { ...prev, user: { ...prev.user, avatarObjectUrl: avatarUrl } } : prev));
                }
            } catch (e) {
                if (cancelled) {
                    return;
                }
                setState({
                    status: 'error',
                    user: null,
                    daysOff: new Set<string>(),
                    etag: null,
                    error: e instanceof Error ? e.message : 'Failed to load caller.',
                });
            }
        })();

        return () => {
            cancelled = true;
            if (mintedAvatarUrl) {
                URL.revokeObjectURL(mintedAvatarUrl);
            }
        };
    }, [configured, org, pat]);

    return state;
};
